import React from 'react';
import PropTypes from 'prop-types';
import { graphql, Link } from 'gatsby';
import { Container, Badge, Carousel, Card, List, Row, Column, HeroVertical } from '@pella/sash-framework';
import { Breadcrumbs, Layout, SEO, CTA, Image } from '../../components';
import { useBranchId } from '../../hooks/use-branch-id';
import BranchPromoPanel from '../../components/common/branch-promo-panel';
import ArticleSlider from '../../components/common/article-slider';
import FAQ from '../../components/common/faq';
import RTAPanel from '../../components/common/rta-panel';
import CardRenderer from '../../components/common/card-types/card-renderer';
import CardSection from '../../components/common/card-section';
import RTAShowroomPanel from '../../components/template/homepage/rta-showroom-panel';
import { isValidArray } from '../../components/utilities/validations';
import './index.css';

const BranchReplacement = (props) => {
	const pageData = props.data.contentstackBranchReplacement;
	const branchId = useBranchId();

	return (
		<Layout
			id='template-BranchReplacement'
			pageTitle={pageData.seo?.title}
		>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.hero && (
				<HeroVertical
					image={pageData.hero.image?.[0]?.image?.gatsbyImageData?.images?.fallback}
					heading={pageData.hero.heading}
					content={pageData.hero.description}
				/>
			)}

			{pageData.modular_blocks?.[0] &&
				pageData.modular_blocks.map((block, index) => {
					if (block.steps != null) {
						return (
							<div
								key={index}
								className='branchReplacement-steps py-6 xl:py-8'
								style={{ backgroundColor: `#${block.steps.background_hex_value}` }}
							>
								<Container>
									{block.steps.heading && (
										<h2
											className='mb-4 md:mb-3'
											dangerouslySetInnerHTML={{ __html: block.steps.heading }}
										/>
									)}
									{isValidArray(block.steps.step) && (
										<div className='grid gap-y-2 md:grid-cols-4'>
											{block.steps.step.map((step, index) => (
												<div
													key={index}
													className='branchReplacement-stepsContainer flex flex-row items-center px-3 py-4 md:block md:h-full md:p-8 md:text-center'
													style={{ backgroundColor: `#${block.steps.container_hex_value}` }}
												>
													{step.heading && (
														<Badge
															color='primary'
															className='mr-3 flex-[0_0_55px] justify-center border-0 text-sm md:mb-5 md:mr-0 md:inline-block md:text-base xl:text-xl'
														>
															{step.heading}
														</Badge>
													)}
													{step.subheading && (
														<div
															className='flex-1 text-base leading-snug tracking-1px xl:text-xl'
															dangerouslySetInnerHTML={{ __html: step.subheading }}
														/>
													)}
												</div>
											))}
										</div>
									)}
								</Container>
							</div>
						);
					}

					if (block.before_after != null && isValidArray(block.before_after.article)) {
						return (
							<div
								key={index}
								className='branchReplacement-beforeAfter pt-4 md:py-6'
							>
								<Container>
									{block.before_after.step_heading && (
										<div className='mb-4 md:flex md:items-center'>
											{block.before_after.step && (
												<Badge
													color='primary'
													className='mb-2.5 mr-4 border-0 text-xs font-normal md:mb-0 md:flex-none md:text-sm xl:text-base'
												>
													{block.before_after.step}
												</Badge>
											)}
											<h2
												className='m-0 text-22px md:flex-1 md:text-28px'
												dangerouslySetInnerHTML={{ __html: block.before_after.step_heading }}
											/>
										</div>
									)}

									<Row
										align='center'
										justify='between'
									>
										<Column
											xs={12}
											md={5}
											xl={4}
											className='mb-5 md:mb-0'
										>
											{block.before_after.heading && (
												<h3
													dangerouslySetInnerHTML={{ __html: block.before_after.heading }}
													className='text-lg md:text-22px'
												/>
											)}
											{block.before_after.description && (
												<div
													dangerouslySetInnerHTML={{ __html: block.before_after.description }}
													className='mt-2 md:mt-3'
												/>
											)}
											{isValidArray(block.before_after.cta) && (
												<CTA
													data={block.before_after.cta[0]}
													className='mt-4 md:mt-6'
												/>
											)}
										</Column>

										<Column
											xs={12}
											md={7}
										>
											<ArticleSlider dynamic={{ manual_entries: block.before_after.article }} />
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (block.local_offer != null && branchId != null) {
						return (
							<BranchPromoPanel
								branchID={branchId}
								key={index}
								id={block.local_offer.anchor_text}
							/>
						);
					}

					if (block.non_global_cards != null) {
						return (
							<div
								key={index}
								className='branchCards-horizontal py-6'
							>
								<Container>
									{block.non_global_cards.step_heading && (
										<div className='mb-2 md:flex md:items-center xl:mb-3'>
											{block.non_global_cards.step && (
												<Badge
													color='primary'
													className='mb-2.5 mr-4 border-0 text-xs font-normal md:mb-0 md:flex-none md:text-sm xl:text-base'
												>
													{block.non_global_cards.step}
												</Badge>
											)}
											<h2
												className='m-0 text-22px md:flex-1 md:text-28px'
												dangerouslySetInnerHTML={{ __html: block.non_global_cards.step_heading }}
											/>
										</div>
									)}
									{block.non_global_cards.description && (
										<div
											className='mb-6'
											dangerouslySetInnerHTML={{ __html: block.non_global_cards.description }}
										/>
									)}

									{isValidArray(block.non_global_cards.group) && (
										<Carousel
											slidePadding={16}
											slidesToShow={{ xs: 1, md: 2, xl: 3 }}
											className='branchCards-slider-group'
											showArrows
										>
											{block.non_global_cards.group.map((card, index) => (
												<Card
													key={index}
													horizontal
												>
													{card.ctas?.length === 1 && (
														<Link
															className='cardLink absolute left-0 top-0 h-full w-full -indent-full'
															to={card.cta[0]?.link.href}
														>
															{card.heading.replace(/(<([^>]+)>)/gi, '')}
														</Link>
													)}

													{isValidArray(card.image) && <Card.Image {...card.image[0].image.gatsbyImageData.images.fallback} />}

													<Card.Content>
														{card.heading && (
															<div
																className='text-base font-semibold md:text-xl'
																dangerouslySetInnerHTML={{ __html: card.heading }}
															/>
														)}
														{isValidArray(card.cta) && (
															<List
																bullets='none'
																className='mt-5 md:mt-6'
															>
																{card.cta.map((cta, index) => (
																	<li key={index}>
																		<CTA
																			data={cta}
																			size={cta.button_style !== 'tertiary' ? 'sm' : null}
																			wide={cta.gtm_link_type === 'button' ? true : null}
																			className={`relative ${cta.button_style === 'tertiary' ? '!text-left' : ''}`}
																		/>
																	</li>
																))}
															</List>
														)}
													</Card.Content>
												</Card>
											))}
										</Carousel>
									)}
								</Container>
							</div>
						);
					}

					if (block.general_content != null) {
						return (
							<div
								key={index}
								className={`branchReplacement-content md:py-6 ${block.general_content.style === 'image-aside' ? 'text-center' : block.general_content.style}`}
								style={{ backgroundColor: `#${block.general_content.background_hex_value}` }}
							>
								<Container className='max-md:px-0'>
									<div
										style={{ backgroundColor: `#${block.general_content.container_hex_value}` }}
										className={`branchReplacement-contentContainer ${block.general_content.container_hex_value ? 'px-6 py-8 md:p-10' : 'py-10'}`}
									>
										<Row align='center'>
											{block.general_content.image?.[0] && (
												<Column
													xs={12}
													md={6}
													className='mb-5 md:mb-0'
												>
													<Image
														data={block.general_content.image[0]}
														className='aspect-[2/1]'
													/>
												</Column>
											)}

											<Column
												xs={12}
												md='fluid'
											>
												{block.general_content.heading && (
													<div
														className={block.general_content.style === 'image-aside' ? '[&>h3]:m-0 [&>h3]:font-normal [&_h3]:text-sm md:[&_h3]:text-lg xl:[&_h3]:text-26px' : ''}
														dangerouslySetInnerHTML={{ __html: block.general_content.heading }}
													/>
												)}
												{block.general_content.description && (
													<div
														className='mt-6 md:mt-8 md:text-xl'
														dangerouslySetInnerHTML={{ __html: block.general_content.description }}
													/>
												)}
												{isValidArray(block.general_content.cta) && (
													<CTA
														data={block.general_content.cta[0]}
														className='mt-6 max-md:p-3 md:mt-8'
													/>
												)}
											</Column>
										</Row>
									</div>
								</Container>
							</div>
						);
					}

					if (block.faqs != null) {
						return (
							<div
								key={index}
								className='branchReplacement-faq py-8 md:py-6'
							>
								<Container>
									{block.faqs.step_heading && (
										<div className='mb-2 md:flex md:items-center xl:mb-3'>
											{block.faqs.step && (
												<Badge
													color='primary'
													className='mb-2.5 mr-4 border-0 text-xs font-normal md:mb-0 md:flex-none md:text-sm xl:text-base'
												>
													{block.faqs.step}
												</Badge>
											)}
											<h2
												className='m-0 text-22px md:flex-1 md:text-28px'
												dangerouslySetInnerHTML={{ __html: block.faqs.step_heading }}
											/>
										</div>
									)}
									{block.faqs.description && (
										<div
											className='mb-2 md:mb-3'
											dangerouslySetInnerHTML={{ __html: block.faqs.description }}
										/>
									)}

									<div
										style={{ backgroundColor: `#${block.faqs.container_hex_value}` }}
										className={`branchReplacement-faqContainer ${block.faqs.container_hex_value ? 'max-md:!bg-transparent md:p-8' : 'pb-4 md:py-8'}`}
									>
										{block.faqs.heading && (
											<h3
												className='text-lg font-semibold md:text-xl'
												dangerouslySetInnerHTML={{ __html: block.faqs.heading }}
											/>
										)}

										{isValidArray(block.faqs.manual_faqs) && (
											<FAQ
												data={block.faqs.manual_faqs}
												multipleColumns
											/>
										)}
									</div>
								</Container>
							</div>
						);
					}

					if (block.rta != null) {
						return (
							<RTAPanel
								key={index}
								data={block.rta}
								className='py-4 md:py-6'
								src='branch-replacement'
							/>
						);
					}

					if (block.articles != null && isValidArray(block.articles.article)) {
						return (
							<div
								key={index}
								className='branchReplacement-articles py-6'
							>
								<Container>
									{block.articles.heading && (
										<h2
											className='mb-2 text-22px !leading-none xl:mb-3 xl:text-28px'
											dangerouslySetInnerHTML={{ __html: block.articles.heading }}
										/>
									)}
									{block.articles.description && (
										<div
											dangerouslySetInnerHTML={{ __html: block.articles.description }}
											className='mb-5 md:mb-4 xl:mb-5'
										/>
									)}

									<Carousel
										slidesToShow={{ xs: 1, md: 2, xl: 3 }}
										showArrows
									>
										{block.articles.article.map((article, index) => (
											<CardRenderer
												key={index}
												data={{
													card_type: 'standard',
													heading: article.top_content.heading,
													image: article.modular_blocks.find((block) => block.all_images != null)?.all_images?.image,
													ctas: [{ link: { href: article.url, title: article.top_content.heading } }],
												}}
												className='[&_ul]:hidden'
											/>
										))}
									</Carousel>
								</Container>
							</div>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
								className='branchReplacement-cards !py-6'
							/>
						);
					}

					if (block.schedule_consult != null) {
						return (
							<RTAShowroomPanel
								data={block.schedule_consult}
								key={index}
							/>
						);
					}
				})}
		</Layout>
	);
};

BranchReplacement.propTypes = {
	data: PropTypes.object,
};

export default BranchReplacement;

// eslint-disable-next-line react/prop-types -- gatsby component
export const Head = ({ data }) => <SEO {...data.contentstackBranchReplacement.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackBranchReplacement(id: { eq: $uid }) {
			url
			breadcrumbs {
				...Breadcrumbs
			}
			hero {
				image {
					...Image
					image {
						url
						gatsbyImageData(quality: 90, layout: FULL_WIDTH)
					}
				}
				heading
				description
			}
			modular_blocks {
				local_offer {
					anchor_text
				}
				steps {
					background_hex_value
					container_hex_value
					heading
					step {
						heading
						subheading
					}
				}
				before_after {
					step
					step_heading
					heading
					description
					cta {
						...CTA
					}
					article {
						url
						top_content {
							heading
							subheading
						}
						modular_blocks {
							all_images {
								image {
									...Image
									image {
										url
										gatsbyImageData(quality: 90)
									}
								}
							}
						}
						category_cta {
							...CTA
						}
					}
				}
				non_global_cards {
					step
					step_heading
					heading
					description
					group {
						heading
						cta {
							...CTA
						}
						image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
					}
				}
				rta {
					anchor_text
					step
					step_heading
				}
				faqs {
					background_hex_value
					step
					step_heading
					container_hex_value
					heading
					description
					manual_faqs {
						question
						answer
					}
				}
				general_content {
					background_hex_value
					container_hex_value
					style
					heading
					description
					cta {
						...CTA
					}
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
				}
				articles {
					heading
					description
					article {
						url
						top_content {
							heading
						}
						modular_blocks {
							all_images {
								image {
									...Image
									image {
										url
										gatsbyImageData(quality: 90)
									}
								}
							}
						}
					}
				}
				cards {
					...CardSection
				}
				schedule_consult {
					heading
				}
			}
			seo {
				...SEO
			}
		}
	}
`;
